import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { createBrowserHistory } from "history";

// Enable detailed logging for debugging Application Insights initialization and usage
const DEBUG_LOGGING = false;

let instance: UserBehaviourLogger;

/**
 * Singleton logger for tracking user behavior and application telemetry using Azure Application Insights.
 * Handles user context, page views, custom events, and metrics while ensuring proper data formatting.
 *
 * @remarks
 * - Requires REACT_APP_APPLICATION_INSIGHTS_CONNECTION_STRING environment variable
 * - Automatically handles route tracking and page visit timing
 * - Optimized for cost with minimal header tracking
 * - Client names are automatically sanitized to meet Application Insights requirements
 */
class UserBehaviourLogger {
  public loggerInstance: ApplicationInsights | undefined;
  reactPlugin: ReactPlugin;
  _setActiveUser: (user: TrackedUserDetails | null) => void;

  constructor() {
    // Enforce singleton pattern
    if (instance) {
      throw new Error(
        "You can only create one instance of the UserBehaviourLogger!"
      );
    }
    instance = this;

    const appInsightsConnectionString =
      process.env.REACT_APP_APPLICATION_INSIGHTS_CONNECTION_STRING;

    this.reactPlugin = new ReactPlugin();

    // Initialize Application Insights if connection string is provided and not disabled
    if (
      appInsightsConnectionString &&
      appInsightsConnectionString.length > 0 &&
      appInsightsConnectionString.toUpperCase().trim() !== "DISABLED"
    ) {
      const browserHistory = createBrowserHistory();

      if (DEBUG_LOGGING) {
        console.log(
          "Initializing Application Insights with connection string:",
          appInsightsConnectionString.substring(0, 20) + "..."
        );
      }

      // *** Add the Click Analytics plug-in. ***
      /* var clickPluginInstance = new ClickAnalyticsPlugin();
               var clickPluginConfig = {
                 autoCapture: true
            }; */
      this.loggerInstance = new ApplicationInsights({
        config: {
          connectionString: appInsightsConnectionString,
          extensions: [this.reactPlugin],
          extensionConfig: {
            [this.reactPlugin.identifier]: { history: browserHistory },
          },
          // Session and route tracking
          enableAutoRouteTracking: true,
          autoTrackPageVisitTime: true,

          // Cost optimization settings
          disableFetchTracking: true,
          enableCorsCorrelation: false,
          enableRequestHeaderTracking: false, // Don't track request headers
          enableResponseHeaderTracking: false, // Don't track response headers

          // Batch settings (standard values)
          enableSessionStorageBuffer: true,
          maxBatchSizeInBytes: 65536,
          maxBatchInterval: 15000,

          // Error tracking
          disableExceptionTracking: false,
        },
      });

      this.loggerInstance.loadAppInsights();
      if (DEBUG_LOGGING) {
        console.log("Application Insights initialized successfully");
      }

      this._setActiveUser = (user: TrackedUserDetails | null) => {
        if (!this.loggerInstance) {
          if (DEBUG_LOGGING) {
            console.error(
              "Cannot set active user, app insights not initialised"
            );
          }
          return;
        }

        // Application Insights requires user ID and account ID (client name) to be free of whitespace and special characters
        this.loggerInstance.setAuthenticatedUserContext(
          user ? user.id.toString() : "unknown",
          this.formatClientName(user?.clientName)
        );
      };
    } else {
      this._setActiveUser = (user: TrackedUserDetails | null) => {
        // Do nothing, app insights not being initialised
        if (DEBUG_LOGGING) {
          console.log("Cannot set active AI user");
        }
      };
    }
  }

  init() {}

  /**
   * Updates the active user context in Application Insights.
   * @param user - The user details to set as active, or null to clear the context
   */
  setActiveUser(user: TrackedUserDetails | null) {
    if (DEBUG_LOGGING) {
      console.log("Setting Application Insights user context:", {
        userId: user?.id,
        clientName: user?.clientName,
        hasLoggerInstance: !!this.loggerInstance,
      });
    }
    this._setActiveUser(user);
  }

  /**
   * Tracks a custom event in Application Insights.
   * @param name - The name of the event
   * @param properties - Optional key-value pairs of additional event data
   */
  trackEvent(name: string, properties?: { [key: string]: any }) {
    if (!this.loggerInstance) {
      if (DEBUG_LOGGING) {
        console.error("Cannot track event, app insights not initialised");
      }
      return;
    }
    this.loggerInstance.trackEvent({ name, properties });
  }

  /**
   * Tracks a page view in Application Insights.
   * @param name - The name of the page
   * @param properties - Optional key-value pairs of additional page data
   */
  trackPageView(name: string, properties?: { [key: string]: any }) {
    if (!this.loggerInstance) {
      if (DEBUG_LOGGING) {
        console.error("Cannot track page view, app insights not initialised");
      }
      return;
    }
    this.loggerInstance.trackPageView({ name, properties });
  }

  /**
   * Tracks a numeric metric in Application Insights.
   * @param name - The name of the metric
   * @param value - The numeric value to track
   * @param properties - Optional key-value pairs of additional metric data
   */
  trackMetric(
    name: string,
    value: number,
    properties?: { [key: string]: any }
  ) {
    if (!this.loggerInstance) {
      if (DEBUG_LOGGING) {
        console.error("Cannot track metric, app insights not initialised");
      }
      return;
    }
    this.loggerInstance.trackMetric({ name, average: value, properties });
  }

  /**
   * Formats a client name to meet Application Insights requirements.
   * @param clientName - The client name to format
   * @returns Formatted client name with no whitespace or special characters, or "unknown" if undefined
   * @remarks
   * - Removes whitespace and special characters (,;=|')
   * - Converts to lowercase
   * - Returns "unknown" if undefined
   */
  private formatClientName(clientName: string | undefined): string {
    if (!clientName) return "unknown";
    return clientName
      .trim()
      .toLowerCase()
      .replace(/[\s,;=|']/g, ""); // Remove whitespace and special characters including apostrophes
  }
}

/**
 * Structure defining the user details that can be tracked in Application Insights.
 */
interface TrackedUserDetails {
  id: number;
  clientName: string;
}

// Create and export a singleton instance of the logger
const singletonLogger = Object.freeze(new UserBehaviourLogger());
export default singletonLogger;
